import Quill from 'quill';
import { EmbedBlot } from 'parchment';
import TableUp, {
  defaultCustomSelect,
  TableAlign,
  TableMenuContextmenu,
  TableResizeBox,
  TableResizeScale,
  TableSelection,
  TableVirtualScrollbar
} from 'quill-table-up';
import { TranslateService } from '@ngx-translate/core';
import { EulaMediaResize } from './eula-media-resize';

const ImageBlot = Quill.import('formats/image') as any;

const MEDIA_PROTOCOLS = ['http', 'https', 'data', 'blob'];
const MEDIA_SIZE_ATTRIBUTES = ['height', 'width'];

function isAllowedMediaUrl(url: string): boolean {
  if (!url) return false;
  const anchor = document.createElement('a');
  anchor.href = url;
  const protocol = anchor.href.slice(0, anchor.href.indexOf(':'));
  return MEDIA_PROTOCOLS.includes(protocol);
}

ImageBlot.sanitize = (url: string) => (isAllowedMediaUrl(url) ? url : '//:0');

export const EULA_LINK_NO_UNDERLINE_CLASS = 'eula-no-underline';

const Link = Quill.import('formats/link') as any;

class EulaLinkBlot extends Link {
  static create(value: unknown) {
    const { href, noUnderline } = linkFormat(value);
    const node = super.create(href) as HTMLAnchorElement;
    node.classList.toggle(EULA_LINK_NO_UNDERLINE_CLASS, noUnderline);
    return node;
  }

  static formats(domNode: HTMLAnchorElement) {
    const href = super.formats(domNode);
    const noUnderline = domNode.classList.contains(EULA_LINK_NO_UNDERLINE_CLASS) || domNode.style.textDecoration === 'none';
    return noUnderline ? { href, noUnderline: true } : href;
  }

  format(name: string, value: unknown) {
    const node = (this as any).domNode as HTMLAnchorElement;
    if (name !== 'link' || !value) {
      super.format(name, value);
      return;
    }

    const parsed = linkFormat(value);
    const noUnderline = typeof value === 'object' ? parsed.noUnderline : node.classList.contains(EULA_LINK_NO_UNDERLINE_CLASS);
    node.setAttribute('href', Link.sanitize(parsed.href));
    node.classList.toggle(EULA_LINK_NO_UNDERLINE_CLASS, noUnderline);
  }
}

function linkFormat(value: unknown): { href: string; noUnderline: boolean } {
  if (typeof value === 'string') return { href: value, noUnderline: false };
  const parsed = value as { href?: unknown; noUnderline?: boolean } | null;
  return { href: String(parsed?.href ?? ''), noUnderline: !!parsed?.noUnderline };
}

class EulaVideoBlot extends EmbedBlot {
  static override blotName = 'video';
  static override className = 'ql-video';
  static override tagName = 'VIDEO';

  static override create(value: string) {
    const node = super.create(value) as HTMLVideoElement;
    node.setAttribute('src', this.sanitize(value));
    node.setAttribute('controls', 'true');
    node.setAttribute('preload', 'metadata');
    node.setAttribute('playsinline', 'true');
    return node;
  }

  static sanitize(url: string) {
    return isAllowedMediaUrl(url) ? url : '';
  }

  static override value(domNode: HTMLVideoElement) {
    return domNode.getAttribute('src');
  }

  static override formats(domNode: HTMLVideoElement) {
    return MEDIA_SIZE_ATTRIBUTES.reduce((formats: Record<string, string>, attribute) => {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute) as string;
      }
      return formats;
    }, {});
  }

  override format(name: string, value: unknown) {
    if (MEDIA_SIZE_ATTRIBUTES.includes(name)) {
      if (value) {
        (this as any).domNode.setAttribute(name, String(value));
      } else {
        (this as any).domNode.removeAttribute(name);
      }
      return;
    }
    super.format(name, value);
  }

  html() {
    return (this as any).domNode.outerHTML;
  }
}

/**
 * Quill 2.x has no built-in table module, so quill-table-up (MIT) must be registered.
 * Registering at module scope means it runs once when this file is imported,
 * before <quill-editor> creates its Quill instance.
 */
const Font = Quill.import('formats/font') as { whitelist: string[] };
const EULA_FONTS = [
  'arial',
  'helvetica',
  'verdana',
  'tahoma',
  'trebuchet',
  'segoe',
  'georgia',
  'times-new-roman',
  'palatino',
  'garamond',
  'courier',
  'lucida',
  'consolas'
];
Font.whitelist = EULA_FONTS;

const icons = Quill.import('ui/icons') as Record<string, unknown>;
icons['code'] = '<svg viewbox="0 0 18 18"><rect class="ql-stroke" x="2" y="5" width="14" height="8" rx="1"></rect><polyline class="ql-even ql-stroke" points="7 7 5 9 7 11"></polyline><polyline class="ql-even ql-stroke" points="11 7 13 9 11 11"></polyline></svg>';
icons['link'] = '<svg viewbox="0 0 24 24"><path class="ql-stroke" d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5-6.5a3 3 0 0 0-6-6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5-6.5"></path></svg>';

Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);
Quill.register({ font: Font }, true);
Quill.register(EulaLinkBlot, true);
Quill.register(EulaVideoBlot, true);
Quill.register('modules/eulaMediaResize', EulaMediaResize, true);

/**
 * Full toolbar: font/size/header, inline formats, colors, script,
 * blockquote/code, lists/indent, align/direction, table, link/image/video, clean.
 */
const EULA_QUILL_TOOLBAR: any[] = [
  [{ font: [false, ...EULA_FONTS] }, { size: ['small', false, 'large', 'huge'] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike', 'code'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  ['blockquote', 'code-block'],
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }, { direction: 'rtl' }],
  [{ [TableUp.toolName]: [] }],
  ['link', 'image', 'video'],
  ['clean']
];

export const EULA_QUILL_MODULES: any = {
  toolbar: EULA_QUILL_TOOLBAR,
  eulaMediaResize: true,
  [TableUp.moduleName]: {
    full: false,
    fullSwitch: true,
    customSelect: defaultCustomSelect,
    customBtn: true,
    autoMergeCell: true,
    modules: [
      { module: TableVirtualScrollbar },
      { module: TableAlign },
      { module: TableResizeBox },
      { module: TableResizeScale },
      { module: TableSelection },
      { module: TableMenuContextmenu }
    ]
  }
};

interface QuillToolbarTooltip {
  selector: string;
  defaultTitle: string;
  key: string;
}

const QUILL_TOOLBAR_TOOLTIPS: QuillToolbarTooltip[] = [
  { selector: '.ql-font', defaultTitle: 'Font', key: 'font' },
  { selector: '.ql-size', defaultTitle: 'Font Size', key: 'font size' },
  { selector: '.ql-header', defaultTitle: 'Heading', key: 'heading' },

  { selector: 'button.ql-bold', defaultTitle: 'Bold (Ctrl+B)', key: 'bold' },
  { selector: 'button.ql-italic', defaultTitle: 'Italic (Ctrl+I)', key: 'italic' },
  { selector: 'button.ql-underline', defaultTitle: 'Underline (Ctrl+U)', key: 'underline' },
  { selector: 'button.ql-strike', defaultTitle: 'Strikethrough', key: 'strikethrough' },
  { selector: 'button.ql-code', defaultTitle: 'Inline Code', key: 'inline code' },

  { selector: '.ql-color', defaultTitle: 'Text Color', key: 'text color' },
  { selector: '.ql-background', defaultTitle: 'Background Color', key: 'background color' },

  { selector: 'button.ql-script[value="sub"]', defaultTitle: 'Subscript', key: 'subscript' },
  { selector: 'button.ql-script[value="super"]', defaultTitle: 'Superscript', key: 'superscript' },

  { selector: 'button.ql-blockquote', defaultTitle: 'Blockquote', key: 'blockquote' },
  { selector: 'button.ql-code-block', defaultTitle: 'Code Block', key: 'code block' },

  { selector: 'button.ql-list[value="ordered"]', defaultTitle: 'Numbered List', key: 'numbered list' },
  { selector: 'button.ql-list[value="bullet"]', defaultTitle: 'Bullet List', key: 'bullet list' },
  { selector: 'button.ql-list[value="check"]', defaultTitle: 'Checklist', key: 'checklist' },

  { selector: 'button.ql-indent[value="-1"]', defaultTitle: 'Decrease Indent', key: 'decrease indent' },
  { selector: 'button.ql-indent[value="+1"]', defaultTitle: 'Increase Indent', key: 'increase indent' },
  { selector: 'button.ql-direction[value="rtl"]', defaultTitle: 'Text Direction (RTL)', key: 'text direction' },

  { selector: '.ql-align', defaultTitle: 'Text Alignment', key: 'text alignment' },

  { selector: '.ql-table-up', defaultTitle: 'Table', key: 'table' },

  { selector: 'button.ql-link', defaultTitle: 'Attach File', key: 'attach file' },
  { selector: 'button.ql-image', defaultTitle: 'Insert Image', key: 'insert image' },
  { selector: 'button.ql-video', defaultTitle: 'Insert Video', key: 'insert video' },

  { selector: 'button.ql-clean', defaultTitle: 'Clear Formatting', key: 'clear formatting' }
];

export function applyQuillToolbarTooltips(toolbarEl: HTMLElement, translate?: TranslateService): void {
  if (!toolbarEl) return;

  for (const item of QUILL_TOOLBAR_TOOLTIPS) {
    const elements = toolbarEl.querySelectorAll<HTMLElement>(item.selector);
    elements.forEach((el) => {
      let label = item.defaultTitle;
      if (translate) {
        const translated = translate.instant(item.key);
        if (translated && translated !== item.key) {
          label = translated;
        }
      }

      const pickerLabel = el.classList.contains('ql-picker') ? el.querySelector<HTMLElement>(':scope > .ql-picker-label') : null;
      const target = pickerLabel ?? el;
      target.setAttribute('title', label);
      target.setAttribute('aria-label', label);
    });
  }
}

